/**
 * Event list helpers shared by the list view and the onboarding landing: my/public
 * classification, active/expired split against the server clock, sorting and the time-left
 * label. Pure module without Vue or moment; the timezone parser is injected as
 * parseInTz(naiveDateTime, timezone) -> milliseconds since epoch.
 */
import { hasDateValue, remainingEventMillis } from './event-time.mjs';

export const SORT_OPTIONS = ['byLastUsed', 'byName', 'byID', 'byStartTime', 'byDistance'];

export const DEFAULT_SORT_ORDER = {
    byLastUsed: 'desc',
    byName: 'asc',
    byID: 'asc',
    byStartTime: 'desc',
    byDistance: 'asc',
};

const STATUS_CREATING = 1;
const STATUS_TESTING = 2;

/** The player's own event: checked in once, or purchased/booked according to the server. */
export function isMyEvent(event) {
    return !!event && (hasDateValue(event.checkin_time) || toBool(event.is_purchased));
}

export function isTestModeEvent(event) {
    const status = event ? Number(event.status_id) : NaN;
    return status === STATUS_CREATING || status === STATUS_TESTING;
}

/** Server clock at nowMs: current_time of the response plus the time elapsed since it arrived. */
export function serverNowMs(event, nowMs, parseInTz) {
    if (event && event.current_time && isFinite(event.received_at)) {
        const serverAtResponse = parseInTz(String(event.current_time), event.timezone);
        if (isFinite(serverAtResponse)) {
            return serverAtResponse + (nowMs - event.received_at);
        }
    }
    return nowMs;
}

export function eventStartMs(event, parseInTz) {
    if (!event || !event.evdatum || !event.evstart) {
        return NaN;
    }
    return parseInTz(event.evdatum + ' ' + event.evstart, event.timezone);
}

/** Remaining play time in ms on the server clock (see event-time.mjs); null without a usable end. */
export function eventTimeLeftMillis(event, nowMs, parseInTz) {
    if (!event) {
        return null;
    }
    const serverNow = serverNowMs(event, nowMs, parseInTz);
    return remainingEventMillis(event, serverNow, (naive) => parseInTz(naive, event.timezone));
}

/** Active = start reached and play time left. An unparsable start does not block. */
export function isEventActive(event, nowMs, parseInTz) {
    if (!event) {
        return false;
    }
    const serverNow = serverNowMs(event, nowMs, parseInTz);
    const start = eventStartMs(event, parseInTz);
    if (isFinite(start) && start > serverNow) {
        return false;
    }
    const left = remainingEventMillis(event, serverNow, (naive) => parseInTz(naive, event.timezone));
    return left !== null && left > 0;
}

/**
 * Splits events into my/public and active/expired. hideMain drops main events, hideInactive
 * drops expired events except those still in creation or test mode.
 */
export function groupEvents(events, { nowMs, parseInTz, hideMain = false, hideInactive = false }) {
    const groups = { my: { active: [], expired: [] }, public: { active: [], expired: [] } };
    (Array.isArray(events) ? events : []).forEach((event) => {
        if (!event) {
            return;
        }
        if (hideMain && toBool(event.is_main_event)) {
            return;
        }
        const active = isEventActive(event, nowMs, parseInTz);
        if (!active && hideInactive && !isTestModeEvent(event)) {
            return;
        }
        const tab = isMyEvent(event) ? groups.my : groups.public;
        (active ? tab.active : tab.expired).push(event);
    });
    return groups;
}

export function visibleRows(groups) {
    return [].concat(groups.my.active, groups.my.expired, groups.public.active, groups.public.expired);
}

/** The event the player is checked into; ids are compared as strings. */
export function isCurrentEvent(event, selectedEventId) {
    return !!event && selectedEventId !== null && selectedEventId !== undefined && String(event.id_event) === String(selectedEventId);
}

/** Sorted copy. Events without a sortable value go last regardless of direction; ties keep their order. */
export function sortEvents(list, option, order, { parseInTz, distanceOf } = {}) {
    const keyOf = sortKeyFn(option, { parseInTz, distanceOf });
    const direction = order === 'desc' ? -1 : 1;
    return (Array.isArray(list) ? list : [])
        .map((event, index) => ({ event, index, key: keyOf(event) }))
        .sort((a, b) => {
            const aMissing = isMissing(a.key);
            const bMissing = isMissing(b.key);
            if (aMissing || bMissing) {
                return aMissing === bMissing ? a.index - b.index : aMissing ? 1 : -1;
            }
            const cmp =
                typeof a.key === 'string'
                    ? a.key.localeCompare(b.key, undefined, { sensitivity: 'base' })
                    : a.key - b.key;
            return cmp !== 0 ? cmp * direction : a.index - b.index;
        })
        .map((entry) => entry.event);
}

const DURATION_LABELS = { d: 'd', h: 'h', m: 'm', s: 's' };

/** Two most significant units, e.g. "2d 3h"; '' when nothing is left. labels overrides the unit labels. */
export function formatDuration(ms, labels = {}) {
    if (ms === null || ms === undefined || !isFinite(ms) || ms <= 0) {
        return '';
    }
    const unitLabels = { ...DURATION_LABELS, ...labels };
    let seconds = Math.floor(ms / 1000);
    const parts = [
        ['d', 86400],
        ['h', 3600],
        ['m', 60],
        ['s', 1],
    ].map(([unit, size]) => {
        const value = Math.floor(seconds / size);
        seconds -= value * size;
        return { label: unitLabels[unit], value };
    });
    const first = parts.findIndex((part) => part.value > 0);
    if (first < 0) {
        return '';
    }
    const second = parts.slice(first + 1).find((part) => part.value > 0);
    return `${parts[first].value}${parts[first].label}` + (second ? ` ${second.value}${second.label}` : '');
}

function sortKeyFn(option, { parseInTz, distanceOf }) {
    switch (option) {
        case 'byName':
            return (event) => (event.evnamedt ? String(event.evnamedt) : null);
        case 'byID':
            return (event) => toNumberOrNull(event.id_event);
        case 'byStartTime':
            return (event) => (parseInTz ? finiteOrNull(eventStartMs(event, parseInTz)) : null);
        case 'byDistance':
            return (event) => (distanceOf ? finiteOrNull(distanceOf(event)) : null);
        case 'byLastUsed':
        default:
            return (event) => {
                if (!hasDateValue(event.checkout_time)) {
                    return null;
                }
                const text = String(event.checkout_time);
                return finiteOrNull(parseInTz ? parseInTz(text, event.timezone) : Date.parse(text));
            };
    }
}

function toBool(value) {
    return value === true || value === 1 || value === '1' || value === 'true';
}

function toNumberOrNull(value) {
    const number = typeof value === 'number' ? value : parseInt(value, 10);
    return isFinite(number) ? number : null;
}

function finiteOrNull(value) {
    return isFinite(value) ? value : null;
}

function isMissing(key) {
    return key === null || key === undefined || key === '';
}
